import type { Localized } from '@/i18n/config'

interface ListingPageCopy {
    seoTitle: string
    seoDescription: string
    eyebrow: string
    title: string
    text: string
    emptyTitle: string
    emptyText: string
}

export interface SiteContentCopy {
    types: {
        insight: string
        post: string
        case_study: string
    }
    listing: {
        allTopics: string
        allIndustries: string
        filterByTopic: string
        filterByIndustry: string
        featured: string
        latest: string
        emptyAction: string
        noMatches: string
    }
    insights: ListingPageCopy
    blog: ListingPageCopy
    caseStudies: ListingPageCopy
    article: {
        breadcrumb: string
        published: string
        readingTime: string
        topic: string
        format: string
        aboutAuthor: string
        writtenBy: string
        linkedin: string
        reportTitle: string
        reportText: string
        downloadReport: string
        relatedEyebrow: string
        relatedTitle: string
        relatedText: string
        backToInsights: string
        backToBlog: string
    }
    caseStudy: {
        client: string
        industry: string
        location: string
        services: string
        outcomes: string
        challenge: string
        approach: string
        results: string
        relatedEyebrow: string
        relatedTitle: string
        backToCaseStudies: string
    }
}

export const siteContent: Localized<SiteContentCopy> = {
    en: {
        types: {
            insight: 'Insight',
            post: 'Blog',
            case_study: 'Case study'
        },
        listing: {
            allTopics: 'All topics',
            allIndustries: 'All industries',
            filterByTopic: 'Filter by topic',
            filterByIndustry: 'Filter by industry',
            featured: 'Featured',
            latest: 'Latest',
            emptyAction: 'Talk to us',
            noMatches: 'Nothing matches this filter yet.'
        },
        insights: {
            seoTitle: 'Insights',
            seoDescription: 'Outlooks, guides and research from Ura Advisory on Kosovo tax, accounting, payroll, international business and the AI-ready finance function.',
            eyebrow: 'Insights',
            title: 'Clear thinking on tax, finance and AI in Kosovo',
            text: 'Outlooks, practical guides and research written by practitioners who have worked inside the tax administration, a Big Four firm and a finance function.',
            emptyTitle: 'Our first insights are on their way',
            emptyText: 'We are preparing outlooks, guides and research for publication. In the meantime, our team is happy to answer your questions directly.'
        },
        blog: {
            seoTitle: 'Blog',
            seoDescription: 'Practical notes from Ura Advisory on Kosovo deadlines, VAT, payroll, monthly accounting and the tools that make finance work easier.',
            eyebrow: 'Blog',
            title: 'Practical notes on deadlines, rules and tools',
            text: 'Short, useful reads for founders, finance managers and HR teams who want to get the monthly details right.',
            emptyTitle: 'New articles are coming soon',
            emptyText: 'We are writing our first practical notes. Until they are published, ask us anything about deadlines, VAT or payroll.'
        },
        caseStudies: {
            seoTitle: 'Case studies',
            seoDescription: 'How Ura Advisory engagements run: finance teams built in Kosovo, tax audits prepared and supported, and VAT compliance put in order.',
            eyebrow: 'Case studies',
            title: 'How our engagements run, from first call to results',
            text: 'The problem we were asked to solve, how we approached it and what changed for the client, measured in outcomes the team can feel every month.',
            emptyTitle: 'Case studies are being prepared',
            emptyText: 'We are documenting recent engagements with our clients. Get in touch and we will walk you through how we would approach your situation.'
        },
        article: {
            breadcrumb: 'Breadcrumb',
            published: 'Published',
            readingTime: 'Reading time',
            topic: 'Topic',
            format: 'Format',
            aboutAuthor: 'About the author',
            writtenBy: 'Written by',
            linkedin: 'LinkedIn profile',
            reportTitle: 'The full report',
            reportText: 'Download the complete report as a file to read offline or share with your team.',
            downloadReport: 'Download the report',
            relatedEyebrow: 'Keep reading',
            relatedTitle: 'Related reading',
            relatedText: 'More from our team on the same questions.',
            backToInsights: 'All insights',
            backToBlog: 'All articles'
        },
        caseStudy: {
            client: 'Client',
            industry: 'Industry',
            location: 'Location',
            services: 'Services',
            outcomes: 'Outcomes',
            challenge: 'The challenge',
            approach: 'Our approach',
            results: 'The results',
            relatedEyebrow: 'More engagements',
            relatedTitle: 'Other case studies',
            backToCaseStudies: 'All case studies'
        }
    },
    sq: {
        types: {
            insight: 'Analizë',
            post: 'Blog',
            case_study: 'Rast studimor'
        },
        listing: {
            allTopics: 'Të gjitha temat',
            allIndustries: 'Të gjitha industritë',
            filterByTopic: 'Filtro sipas temës',
            filterByIndustry: 'Filtro sipas industrisë',
            featured: 'Në fokus',
            latest: 'Më të rejat',
            emptyAction: 'Na kontaktoni',
            noMatches: 'Ende nuk ka asgjë për këtë filtër.'
        },
        insights: {
            seoTitle: 'Analiza',
            seoDescription: 'Perspektiva, udhëzues dhe studime nga Ura Advisory për tatimet, kontabilitetin, pagat dhe biznesin ndërkombëtar në Kosovë, si dhe për funksionin financiar të gatshëm për AI.',
            eyebrow: 'Analiza',
            title: 'Mendim i qartë për tatimet, financat dhe AI në Kosovë',
            text: 'Perspektiva, udhëzues praktikë dhe studime të shkruara nga profesionistë që kanë punuar brenda administratës tatimore, në një firmë të Big Four dhe në një funksion financiar.',
            emptyTitle: 'Analizat tona të para janë në përgatitje',
            emptyText: 'Po përgatisim perspektiva, udhëzues dhe studime për publikim. Ndërkohë, ekipi ynë u përgjigjet me kënaqësi pyetjeve tuaja drejtpërdrejt.'
        },
        blog: {
            seoTitle: 'Blog',
            seoDescription: 'Shënime praktike nga Ura Advisory për afatet në Kosovë, TVSH-në, pagat, kontabilitetin mujor dhe mjetet që e lehtësojnë punën financiare.',
            eyebrow: 'Blog',
            title: 'Shënime praktike për afatet, rregullat dhe mjetet',
            text: "Lexime të shkurtra dhe të dobishme për themelues, menaxherë financash dhe ekipe të burimeve njerëzore që duan t'i bëjnë mirë detajet mujore.",
            emptyTitle: 'Artikujt e rinj vijnë së shpejti',
            emptyText: 'Po shkruajmë shënimet tona të para praktike. Deri sa të publikohen, na pyesni për çdo gjë rreth afateve, TVSH-së apo pagave.'
        },
        caseStudies: {
            seoTitle: 'Raste studimore',
            seoDescription: 'Si zhvillohen angazhimet e Ura Advisory: ekipe financiare të ngritura në Kosovë, kontrolle tatimore të përgatitura dhe të mbështetura, dhe përputhshmëri e rregulluar me TVSH.',
            eyebrow: 'Raste studimore',
            title: 'Si zhvillohen angazhimet tona, nga biseda e parë te rezultatet',
            text: 'Problemi që na u kërkua të zgjidhnim, si iu qasëm dhe çfarë ndryshoi për klientin, e matur me rezultate që ekipi i ndien çdo muaj.',
            emptyTitle: 'Rastet studimore janë në përgatitje',
            emptyText: "Po dokumentojmë angazhimet e fundit me klientët tanë. Na kontaktoni dhe do t'ju shpjegojmë si do t'i qaseshim situatës suaj."
        },
        article: {
            breadcrumb: 'Navigimi',
            published: 'Publikuar',
            readingTime: 'Koha e leximit',
            topic: 'Tema',
            format: 'Formati',
            aboutAuthor: 'Rreth autorit',
            writtenBy: 'Shkruar nga',
            linkedin: 'Profili në LinkedIn',
            reportTitle: 'Raporti i plotë',
            reportText: 'Shkarkoni raportin e plotë si skedar për ta lexuar jashtë linje ose për ta ndarë me ekipin tuaj.',
            downloadReport: 'Shkarko raportin',
            relatedEyebrow: 'Vazhdoni leximin',
            relatedTitle: 'Lexime të ngjashme',
            relatedText: 'Më shumë nga ekipi ynë për të njëjtat pyetje.',
            backToInsights: 'Të gjitha analizat',
            backToBlog: 'Të gjithë artikujt'
        },
        caseStudy: {
            client: 'Klienti',
            industry: 'Industria',
            location: 'Vendndodhja',
            services: 'Shërbimet',
            outcomes: 'Rezultatet',
            challenge: 'Sfida',
            approach: 'Qasja jonë',
            results: 'Rezultatet',
            relatedEyebrow: 'Më shumë angazhime',
            relatedTitle: 'Raste të tjera studimore',
            backToCaseStudies: 'Të gjitha rastet studimore'
        }
    }
}
